import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type AccountDetail, type BcDoc, type Customer360 } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox, Severity, StatePill, ago, label } from "../ui";
import { Icon } from "../icons";
import { bcUrl } from "../config";
import { XSection } from "../crm";
import { Peek, type PeekTarget } from "../Peek";

const money = (n: number | null | undefined, cur?: string | null) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(n);
const day = (d: string | null) => (d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const where = (a: { city: string | null; region: string | null; country: string | null }) => [a.city, a.region, a.country].filter(Boolean).join(", ");

/** What to tell the user when Business Central can't be reached, with the fix. */
export function BcNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const code = error instanceof ApiError ? error.body?.code : undefined;
  const text: Record<string, string> = {
    not_configured: "Business Central isn't connected yet.",
    consent_required: "Business Central needs an admin to grant courageMFG access (one-time consent).",
    auth_failed: "Business Central refused access for your account.",
  };
  return (
    <div className="msg warn" role="status" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <span><b>{text[code ?? ""] ?? "Couldn't reach Business Central."}</b> {code ? "" : error instanceof Error ? error.message : ""}</span>
      <Link to="/settings/integrations" className="btn" style={{ marginLeft: "auto" }}>Integrations</Link>
    </div>
  );
}

const LIFECYCLES = [{ id: "", name: "All" }, { id: "prospect", name: "Prospects" }, { id: "qualified", name: "Qualified" }, { id: "customer", name: "Customers" }, { id: "inactive", name: "Inactive" }];

export function AccountsList() {
  const [params, setParams] = useSearchParams();
  const lc = params.get("lifecycle") ?? "";
  const [q, setQ] = useState("");
  const { data, error } = useLoad(() => api.accounts(lc || undefined), [lc]);
  const rows = (data ?? []).filter((a) => !q || (a.name + a.number + a.customers.join(" ") + (a.city ?? "")).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <CommandBar status={data ? `${data.length} account${data.length === 1 ? "" : "s"}` : ""}>
        <Link className="cmd primary" to="/accounts/new"><Icon name="plus" size={14} />New account</Link>
        <Link className="cmd" to="/accounts/import">Import from Business Central</Link>
      </CommandBar>
      <div className="page">
        <nav className="crumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><span>/</span><Link to="/a/commercial">CRM</Link><span>/</span><span>Accounts</span></nav>
        <div className="page-title"><h1 className="serif" style={{ fontSize: 30 }}>Accounts</h1><span className="sub">Prospects and customers. Once qualified, an account becomes a customer in Business Central.</span></div>
        <div className="pivot" role="tablist">
          {LIFECYCLES.map((t) => <button key={t.id} role="tab" aria-selected={lc === t.id} onClick={() => setParams(t.id ? { lifecycle: t.id } : {})}>{t.name}</button>)}
        </div>
        <div className="filters" style={{ marginBottom: 12 }}><input className="input" style={{ maxWidth: 320 }} placeholder="Filter by name, number or BC customer" aria-label="Filter accounts" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <ErrorBox error={error} />
        <div className="dash-card" style={{ padding: 0 }}>
          {!data ? <p className="dash-empty dash-skeleton" style={{ margin: 16 }}>Loading</p> : rows.length ? (
            <div className="table-wrap">
              <table className="dgrid">
                <thead><tr><th>Number</th><th>Name</th><th>Lifecycle</th><th>Location</th><th>BC customers</th><th>Updated</th></tr></thead>
                <tbody>{rows.map((a) => (
                  <tr key={a.id}>
                    <td><Link to={`/accounts/${a.number}`} className="mono">{a.number}</Link></td>
                    <td style={{ fontWeight: 600 }}><Link to={`/accounts/${a.number}`} style={{ color: "inherit" }}>{a.name}</Link></td>
                    <td><StatePill state={a.lifecycle} /></td>
                    <td className="muted">{where(a) || "—"}</td>
                    <td>{a.customers.length ? a.customers.map((n) => <span key={n} className="chip-bc" style={{ marginRight: 4 }}><span className="k">BC</span><span className="v">{n}</span></span>) : <span className="muted">—</span>}</td>
                    <td className="muted">{ago(a.updatedAt)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : (
            <div className="card-body">
              <p style={{ marginTop: 0 }}>{data.length ? "No accounts match." : "No accounts yet."}</p>
              {!data.length && <div className="btn-row"><Link className="btn primary" to="/accounts/new">New account</Link><Link className="btn" to="/accounts/import">Import your BC customers</Link></div>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const FIELDS: { k: "industry" | "website" | "phone" | "email" | "addressLine1" | "city" | "region" | "postalCode" | "country"; label: string; half?: boolean; placeholder?: string }[] = [
  { k: "industry", label: "Industry", half: true, placeholder: "e.g. Industrial machinery" }, { k: "website", label: "Website", half: true, placeholder: "example.com" },
  { k: "phone", label: "Phone", half: true }, { k: "email", label: "Email", half: true },
  { k: "addressLine1", label: "Address" }, { k: "city", label: "City", half: true }, { k: "region", label: "State", half: true, placeholder: "CA" },
  { k: "postalCode", label: "Postal code", half: true }, { k: "country", label: "Country", half: true, placeholder: "US" },
];

function AccountForm({ initial, onSave, saving, submitLabel }: { initial?: Partial<AccountDetail>; onSave: (v: Record<string, string>) => void; saving: boolean; submitLabel: string }) {
  const [v, setV] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = { name: initial?.name ?? "", notes: initial?.notes ?? "" };
    for (const f of FIELDS) o[f.k] = (initial?.[f.k] as string) ?? "";
    return o;
  });
  const set = (k: string) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); onSave(v); }}>
      <div className="field"><label htmlFor="a-name">Company name</label><input id="a-name" className="input" required value={v.name} onChange={set("name")} /></div>
      <div className="form-grid">
        {FIELDS.map((f) => (
          <div key={f.k} className={`field ${f.half ? "" : "full"}`}><label htmlFor={`a-${f.k}`}>{f.label}</label><input id={`a-${f.k}`} className="input" placeholder={f.placeholder} value={v[f.k]} onChange={set(f.k)} /></div>
        ))}
      </div>
      <div className="field"><label htmlFor="a-notes">Notes</label><textarea id="a-notes" className="textarea" value={v.notes} onChange={set("notes")} /></div>
      <button className="btn primary" disabled={saving || !v.name.trim()}>{submitLabel}</button>
    </form>
  );
}

export function NewAccount() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <>
      <CommandBar><Link className="cmd" to="/accounts">← Accounts</Link></CommandBar>
      <div className="page" style={{ maxWidth: 760 }}>
        <div className="page-title"><h1 className="serif" style={{ fontSize: 30 }}>New account</h1><span className="sub">Starts as a prospect. Qualify it, then convert it to create the customer in Business Central.</span></div>
        <ErrorBox error={error} />
        <div className="dash-card">
          <AccountForm saving={saving} submitLabel="Create account" onSave={async (v) => {
            setSaving(true); setError(null);
            try { const a = await api.createAccount(v); nav(`/accounts/${a.number}`); } catch (e) { setError(e); setSaving(false); }
          }} />
        </div>
      </div>
    </>
  );
}

function CustomerPicker({ onPick, busy, cta }: { onPick: (numbers: string[]) => void; busy: boolean; cta: (n: number) => string }) {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [sel, setSel] = useState<string[]>([]);
  useEffect(() => { const t = setTimeout(() => setTerm(q), 300); return () => clearTimeout(t); }, [q]);
  const { data, error } = useLoad(() => api.bcCustomers(term), [term]);
  return (
    <>
      <div className="btn-row" style={{ marginBottom: 10 }}>
        <input className="input" style={{ maxWidth: 360 }} placeholder="Search BC customers by name or number" aria-label="Search Business Central customers" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn primary" disabled={!sel.length || busy} onClick={() => onPick(sel)}>{cta(sel.length)}</button>
      </div>
      <BcNotice error={error} />
      {data && (
        <div className="table-wrap">
          <table className="dgrid">
            <thead><tr><th aria-label="Select" /><th>BC customer</th><th>Name</th><th>Location</th><th style={{ textAlign: "right" }}>Balance due</th><th>Account</th></tr></thead>
            <tbody>{data.map((c) => (
              <tr key={c.id}>
                <td><input type="checkbox" aria-label={`Select ${c.displayName}`} disabled={!!c.account} checked={sel.includes(c.number)} onChange={(e) => setSel(e.target.checked ? [...sel, c.number] : sel.filter((x) => x !== c.number))} /></td>
                <td className="mono">{c.number}</td>
                <td style={{ fontWeight: 600 }}>{c.displayName}{c.blocked ? <span className="status danger" style={{ marginLeft: 6 }}>Blocked</span> : null}</td>
                <td className="muted">{[c.city, c.state, c.country].filter(Boolean).join(", ") || "—"}</td>
                <td style={{ textAlign: "right" }}>{money(c.balanceDue)}</td>
                <td>{c.account ? <Link to={`/accounts/${c.account.number}`}>{c.account.number}</Link> : <span className="muted">—</span>}</td>
              </tr>
            ))}</tbody>
          </table>
          {!data.length && <p className="dash-empty" style={{ margin: 16 }}>No BC customers match.</p>}
        </div>
      )}
    </>
  );
}

export function ImportAccounts() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<{ created: { number: string; account: string }[]; skipped: string[] } | null>(null);
  return (
    <>
      <CommandBar><Link className="cmd" to="/accounts">← Accounts</Link></CommandBar>
      <div className="page">
        <div className="page-title"><h1 className="serif" style={{ fontSize: 30 }}>Import from Business Central</h1><span className="sub">Each BC customer you pick becomes an account, already a customer and linked to BC. Customers already on an account are skipped.</span></div>
        <ErrorBox error={error} />
        {done && <div className="msg info">Imported {done.created.length} account{done.created.length === 1 ? "" : "s"}{done.skipped.length ? `; skipped ${done.skipped.join(", ")}` : ""}. <Link to="/accounts?lifecycle=customer">See customers</Link></div>}
        <div className="dash-card">
          <CustomerPicker busy={busy} cta={(n) => (n ? `Import ${n}` : "Import")} onPick={async (numbers) => {
            setBusy(true); setError(null);
            try { const r = await api.importAccounts(numbers); setDone(r); if (r.created.length === 1) nav(`/accounts/${r.created[0].account}`); } catch (e) { setError(e); } finally { setBusy(false); }
          }} />
        </div>
      </div>
    </>
  );
}

/* ---------- Account 360 ---------- */

const LIFE = [{ id: "prospect", name: "Prospect" }, { id: "qualified", name: "Qualified" }, { id: "customer", name: "Customer", bc: true }];

function Lifecycle({ a }: { a: AccountDetail }) {
  const idx = LIFE.findIndex((s) => s.id === a.lifecycle);
  const when = (id: string) => [...a.history].reverse().find((h) => h.toState === id)?.at;
  return (
    <div className="bpf" role="list" aria-label="Account lifecycle">
      {LIFE.map((s, i) => {
        const cls = a.lifecycle === "inactive" ? "" : i < idx ? "done" : i === idx ? "current" : "";
        const w = when(s.id);
        return (
          <div key={s.id} role="listitem" className={`bpf-stage ${cls}`} aria-current={i === idx ? "step" : undefined}>
            <span className="name">{i < idx ? "✓ " : ""}{s.bc && <span className="tag bc" style={{ marginRight: 6 }}>BC</span>}{s.name}</span>
            <span className="meta">{w ? ago(w) : s.bc ? "Customer created in BC" : ""}</span>
          </div>
        );
      })}
      {a.lifecycle === "inactive" && <span className="bpf-stage branch"><span className="name">Inactive</span></span>}
    </div>
  );
}

function DocTable({ rows, empty, kind }: { rows: BcDoc[]; empty: string; kind: string }) {
  if (!rows.length) return <p className="dash-empty" style={{ margin: "6px 0 2px" }}>{empty}</p>;
  return (
    <table className="dgrid compact">
      <thead><tr><th>{kind}</th><th>Date</th><th>Status</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
      <tbody>{rows.map((d) => (
        <tr key={d.id}><td className="mono">{d.number}</td><td className="muted">{day(d.date)}</td><td>{d.status ? <span className={`status ${/open|due/i.test(d.status) ? "warn" : /paid|ship/i.test(d.status) ? "ok" : ""}`}>{d.status}</span> : "—"}</td><td style={{ textAlign: "right" }}>{money(d.amount, d.currency)}</td></tr>
      ))}</tbody>
    </table>
  );
}

const ok = <T,>(p: { ok: boolean; data?: T } | undefined, fallback: T): T => (p && p.ok ? (p as any).data : fallback);

export function AccountPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: a, error, reload } = useLoad(() => api.account(id), [id]);
  const hasCustomers = !!a?.customers.length;
  const { data: bc, error: bcError, reload: reloadBc } = useLoad(() => (a && a.customers.length ? api.accountBc(a.id) : Promise.resolve(null)), [a?.id, a?.customers.length]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [panel, setPanel] = useState<"" | "edit" | "link" | "contact">("");
  const [peek, setPeek] = useState<PeekTarget | null>(null);
  const [contact, setContact] = useState({ name: "", title: "", email: "", phone: "", isPrimary: false });

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (!a) return <div className="page"><div className="empty">Loading…</div></div>;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setActionError(null);
    try { await fn(); setPanel(""); await reload(); await reloadBc(); } catch (e) { setActionError(e); } finally { setBusy(false); }
  };
  const c360 = (bc?.customers ?? []).filter((x): x is Customer360 => "customer" in x);
  const all = <K extends "orders" | "quotes" | "invoices" | "creditMemos" | "shipments">(k: K) => c360.flatMap((x) => ok(x[k], [] as BcDoc[]));
  const balance = c360.reduce((s, x) => s + (ok(x.financials, { balance: 0 } as any).balance ?? 0), 0);
  const overdue = c360.reduce((s, x) => s + (ok(x.financials, { overdueAmount: 0 } as any).overdueAmount ?? 0), 0);
  const sales = c360.reduce((s, x) => s + (ok(x.financials, { totalSales: 0 } as any).totalSales ?? 0), 0);
  const invoices = all("invoices");
  // Posted and unpaid are receivables; a draft invoice isn't posted yet, so it's shown on its own.
  const openInv = invoices.filter((d) => /open/i.test(d.status ?? ""));
  const draftInv = invoices.filter((d) => /draft/i.test(d.status ?? ""));
  const pastInv = invoices.filter((d) => !/open|draft/i.test(d.status ?? ""));
  const orders = all("orders");
  const quotes = all("quotes");
  const done = ["closed", "cancelled", "escalated"];
  const recActive = a.records.filter((r) => !done.includes(r.state));
  const recHistory = a.records.filter((r) => done.includes(r.state));
  const bcContacts = c360.flatMap((x) => ok(x.contacts, []));
  const src = bc?.company ? `Business Central · ${bc.company}` : "Business Central";
  const openOpps = (a.opportunities ?? []).filter((o) => !["won", "lost"].includes(o.stage));
  const closedOpps = (a.opportunities ?? []).filter((o) => ["won", "lost"].includes(o.stage));
  const openQuotes = (a.quotes ?? []).filter((q) => ["draft", "approved", "sent"].includes(q.state));

  const today = new Date().toISOString().slice(0, 10);
  const overdueInv = openInv.filter((d) => d.dueDate && d.dueDate < today && (d.remaining ?? d.amount ?? 0) > 0);
  const oldest = [...overdueInv].sort((x, y) => (x.dueDate! < y.dueDate! ? -1 : 1))[0];
  const daysOver = (d: BcDoc) => (d.dueDate ? Math.max(0, Math.round((Date.parse(today) - Date.parse(d.dueDate)) / 864e5)) : 0);
  const lifeIdx = LIFE.findIndex((s) => s.id === a.lifecycle);
  const usd = (n: number | null | undefined) => (n === null || n === undefined ? "" : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const usDate = (d: string | null | undefined) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "");
  const docTable = (rows: BcDoc[], kind: "order" | "quote" | "invoice" | "creditMemo" | "shipment", noLabel: string, emptyText: string, dueCols = false) => rows.length ? (
    <div className="table-wrap">
      <table className="dgrid num-grid">
        <thead><tr><th>{noLabel}</th><th>Date</th><th>Status</th>{dueCols && <th>Due Date</th>}{dueCols && <th className="r">Days Overdue</th>}<th className="r">Amount</th>{dueCols && <th className="r">Remaining Amount</th>}</tr></thead>
        <tbody>{rows.map((d) => (
          <tr key={d.id} className="peekable" onClick={() => setPeek({ type: "bc", kind, id: d.id, number: d.number })}><td><button className="linkish rowlink" onClick={(e) => { e.stopPropagation(); setPeek({ type: "bc", kind, id: d.id, number: d.number }); }}>{d.number}</button></td><td>{usDate(d.date)}</td><td>{d.status ?? ""}</td>
            {dueCols && <td>{usDate(d.dueDate)}</td>}{dueCols && <td className={`r ${daysOver(d) > 0 ? "bad" : ""}`}>{daysOver(d) || ""}</td>}
            <td className="r">{usd(d.amount)}</td>{dueCols && <td className="r">{usd(d.remaining)}</td>}</tr>
        ))}</tbody>
      </table>
    </div>
  ) : <p className="dash-empty" style={{ margin: 0 }}>{emptyText}</p>;
  const A = (p: string) => <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d={p} fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>;

  return (
    <>
      <CommandBar status={`Updated ${ago(a.updatedAt)}`}>
        <button className="cmd" onClick={() => setPanel(panel === "edit" ? "" : "edit")}>{A("M3 13l1-3 7-7 2 2-7 7-3 1z")}Edit</button>
        {a.lifecycle === "prospect" && <button className="cmd" disabled={busy} onClick={() => run(() => api.transition(a.id, "qualified", "Qualified"))}>{A("M3 8.5l3 3 7-7")}Qualify</button>}
        {a.lifecycle === "qualified" && <button className="cmd" disabled={busy} onClick={() => run(() => api.convertAccount(a.id))}>{A("M3 8h9M9 4l4 4-4 4")}Create Customer in BC</button>}
        <Link className="cmd" to={`/opportunities/new?account=${a.number}`}>{A("M8 3v10M3 8h10")}New Opportunity</Link>
        <button className="cmd" onClick={() => setPanel(panel === "contact" ? "" : "contact")}>{A("M8 3v10M3 8h10")}New Contact</button>
        <button className="cmd" onClick={() => setPanel(panel === "link" ? "" : "link")}>{A("M6 10l4-4M5 7L3.5 8.5a2.5 2.5 0 003.5 3.5L8.5 10.5M11 9l1.5-1.5A2.5 2.5 0 009 4L7.5 5.5")}Link BC Customer</button>
        <span className="cmd-sep" aria-hidden="true" />
        {a.customers[0] && <a className="cmd" href={`${bcUrl(21)}&filter=${encodeURIComponent(`'No.' IS '${a.customers[0].number}'`)}`} target="_blank" rel="noopener noreferrer">{A("M9 2.5h4.5V7M13.5 2.5L7 9M11 9.5v4H2.5V5h4")}Open Customer Card in BC</a>}
        {a.lifecycle === "qualified" && <button className="cmd" disabled={busy} onClick={() => run(() => api.transition(a.id, "prospect"))}>Back to Prospect</button>}
        {a.lifecycle !== "inactive" ? <button className="cmd" disabled={busy} onClick={() => run(() => api.transition(a.id, "inactive"))}>Mark Inactive</button>
          : <button className="cmd" disabled={busy} onClick={() => run(() => api.transition(a.id, "prospect"))}>Reactivate</button>}
        <button className="cmd" onClick={() => { reload(); reloadBc(); }}>Refresh</button>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title">
          <Link to="/accounts" className="crumb">Accounts</Link>
          <div className="line">
            <h1>{a.number} · {a.name}</h1>
            <span className="meta">Lifecycle: {label(a.lifecycle)}</span>
            {a.customers.length > 0 && <span className="meta">BC Customer No.: {a.customers.map((c) => c.number).join(", ")}</span>}
          </div>
          {a.lifecycle !== "inactive" && (
            <div className="stage-steps" aria-label="Account lifecycle">
              {LIFE.map((st, i) => <span key={st.id} className={`st ${i < lifeIdx ? "done" : i === lifeIdx ? "cur" : ""}`}>{i < lifeIdx ? "✓ " : ""}{st.name}{st.bc ? " (in BC)" : ""}</span>)}
            </div>
          )}
        </div>

        {overdueInv.length > 0 && (
          <div className="notify warn" role="status">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l7 13H1z" fill="#BC4B09" /><path d="M8 6v4M8 11.5v1" stroke="#fff" strokeWidth="1.4" /></svg>
            <span>{overdueInv.length} posted {overdueInv.length === 1 ? "invoice is" : "invoices are"} past due, {usd(overdueInv.reduce((t, d) => t + (d.remaining ?? d.amount ?? 0), 0))} USD in total.{oldest ? ` The oldest, ${oldest.number}, is ${daysOver(oldest)} days overdue.` : ""}</span>
            <a href="#accounting">Show in Accounting</a>
          </div>
        )}
        <BcNotice error={bcError} />
        <ErrorBox error={actionError instanceof ApiError && actionError.body?.code ? null : actionError} />
        {actionError instanceof ApiError && actionError.body?.code && <BcNotice error={actionError} />}

        {panel === "edit" && (
          <div className="dash-card">
            <div className="dash-card-h" style={{ marginBottom: 12 }}>Edit Account</div>
            <AccountForm initial={a} saving={busy} submitLabel="Save" onSave={(v) => run(() => api.updateAccount(a.id, v))} />
          </div>
        )}
        {panel === "contact" && (
          <form className="dash-card" onSubmit={(e) => { e.preventDefault(); run(async () => { await api.addContact(a.id, contact); setContact({ name: "", title: "", email: "", phone: "", isPrimary: false }); }); }}>
            <div className="dash-card-h" style={{ marginBottom: 12 }}>New Contact</div>
            <div className="form-grid">
              {([["name", "Name"], ["title", "Job Title"], ["email", "Email"], ["phone", "Phone No."]] as const).map(([k, l]) => <div key={k} className="field"><label htmlFor={`c-${k}`}>{l}</label><input id={`c-${k}`} className="input" required={k === "name"} value={contact[k]} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} /></div>)}
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}><input type="checkbox" checked={contact.isPrimary} onChange={(e) => setContact({ ...contact, isPrimary: e.target.checked })} />Primary contact</label>
            <button className="btn primary" disabled={busy || !contact.name.trim()}>Save Contact</button>
          </form>
        )}
        {panel === "link" && (
          <div className="dash-card">
            <div className="dash-card-h">Link BC Customer</div>
            <div className="dash-cap" style={{ marginBottom: 10 }}>For a sell-to or bill-to customer that already exists in Business Central. Linking makes this account a customer.</div>
            <CustomerPicker busy={busy} cta={(n) => (n ? `Link ${n}` : "Link")} onPick={(numbers) => run(async () => { for (const n of numbers) await api.linkCustomer(a.id, n); })} />
          </div>
        )}

        <div className="card-cols">
          <div className="tabs">
            <XSection title="General">
              <div className="bc-fields">
                <dl>
                  <dt>No.</dt><dd>{a.number}</dd>
                  <dt>Name</dt><dd>{a.name}</dd>
                  <dt>Address</dt><dd>{[a.addressLine1, a.city, a.region, a.postalCode, a.country].filter(Boolean).join(", ")}</dd>
                  <dt>Phone No.</dt><dd>{a.phone ?? ""}</dd>
                  <dt>Email</dt><dd>{a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : ""}</dd>
                  <dt>Website</dt><dd>{a.website ?? ""}</dd>
                </dl>
                <dl>
                  <dt>Industry</dt><dd>{a.industry ?? ""}</dd>
                  <dt>Lifecycle</dt><dd>{label(a.lifecycle)}</dd>
                  <dt>BC Customer No.</dt><dd>{a.customers.length ? a.customers.map((cst) => (
                    <span key={cst.id} style={{ marginRight: 10 }}>
                      <a href={`${bcUrl(21)}&filter=${encodeURIComponent(`'No.' IS '${cst.number}'`)}`} target="_blank" rel="noopener noreferrer">{cst.number}</a>{" "}
                      <button className="linkish" onClick={() => run(() => api.unlinkCustomer(a.id, cst.id))} aria-label={`Unlink ${cst.number}`}>Unlink</button>
                    </span>
                  )) : <span className="muted">{a.lifecycle === "qualified" ? "Use Create Customer in BC, or Link BC Customer." : a.lifecycle === "prospect" ? "Created in BC once the account is qualified." : "None linked."}</span>}</dd>
                  <dt>Ship-to Addresses</dt><dd>{hasCustomers ? <a href={bcUrl(301)} target="_blank" rel="noopener noreferrer">In Business Central</a> : ""}</dd>
                  <dt>Notes</dt><dd style={{ whiteSpace: "pre-wrap" }}>{a.notes ?? ""}</dd>
                </dl>
              </div>
            </XSection>

            <XSection title="Sales" chips={[`${openOpps.length} open ${openOpps.length === 1 ? "opportunity" : "opportunities"}`, `${openQuotes.length} open ${openQuotes.length === 1 ? "quote" : "quotes"}`, hasCustomers ? `${orders.length} ${orders.length === 1 ? "sales order" : "sales orders"}` : null]}>
              <h3 className="sub-h2">Open Opportunities</h3>
              {openOpps.length ? (
                <div className="table-wrap"><table className="dgrid num-grid">
                  <thead><tr><th>No.</th><th>Description</th><th>Stage</th><th className="r">Estimated Value</th><th>Expected Closing Date</th></tr></thead>
                  <tbody>{openOpps.map((o) => <tr key={o.id} className="peekable" onClick={() => setPeek({ type: "opportunity", number: o.number })}><td><button className="linkish rowlink" onClick={(e) => { e.stopPropagation(); setPeek({ type: "opportunity", number: o.number }); }}>{o.number}</button></td><td>{o.name}</td><td>{label(o.stage)}</td><td className="r">{usd(o.amount)}</td><td>{usDate(o.closeDate)}</td></tr>)}</tbody>
                </table></div>
              ) : <p className="dash-empty" style={{ margin: 0 }}>None open. <Link to={`/opportunities/new?account=${a.number}`}>New Opportunity</Link></p>}
              <h3 className="sub-h2">Quotes</h3>
              {(a.quotes ?? []).length ? (
                <div className="table-wrap"><table className="dgrid num-grid">
                  <thead><tr><th>No.</th><th>Opportunity</th><th>BC Quote No.</th><th>Status</th><th className="r">Amount Excl. Tax</th><th>Updated</th></tr></thead>
                  <tbody>{(a.quotes ?? []).map((q) => <tr key={q.id} className="peekable" onClick={() => setPeek({ type: "quote", number: q.number })}><td><button className="linkish rowlink" onClick={(e) => { e.stopPropagation(); setPeek({ type: "quote", number: q.number }); }}>{q.number}</button>{q.isPrimary ? " (Primary)" : ""}</td><td>{q.opportunityName ?? ""}</td><td>{q.bcQuoteNumber ?? ""}</td><td>{label(q.state)}</td><td className="r">{usd(q.total)}</td><td>{usDate(q.updatedAt)}</td></tr>)}</tbody>
                </table></div>
              ) : <p className="dash-empty" style={{ margin: 0 }}>No quotes yet.</p>}
              {hasCustomers && <><h3 className="sub-h2">Sales Orders</h3>{docTable(orders, "order", "No.", bc ? "No open sales orders." : "Loading from Business Central…")}</>}
              {hasCustomers && quotes.length > 0 && <><h3 className="sub-h2">Sales Quotes in Business Central</h3>{docTable(quotes, "quote", "No.", "")}</>}
              {closedOpps.length > 0 && <><h3 className="sub-h2">Won and Lost Opportunities</h3>
                <div className="table-wrap"><table className="dgrid num-grid">
                  <thead><tr><th>No.</th><th>Description</th><th>Status</th><th>BC Sales Order</th><th className="r">Amount</th></tr></thead>
                  <tbody>{closedOpps.map((o) => <tr key={o.id} className="peekable" onClick={() => setPeek({ type: "opportunity", number: o.number })}><td><button className="linkish rowlink" onClick={(e) => { e.stopPropagation(); setPeek({ type: "opportunity", number: o.number }); }}>{o.number}</button></td><td>{o.name}</td><td>{label(o.stage)}</td><td>{o.bcOrderNumber ?? ""}</td><td className="r">{usd(o.amount)}</td></tr>)}</tbody>
                </table></div></>}
            </XSection>

            <XSection title="Customer Service" chips={[`${recActive.length} open ${recActive.length === 1 ? "issue or CAPA" : "issues and CAPAs"}`, recHistory.length ? `${recHistory.length} closed` : null]} open={recActive.length > 0}>
              {recActive.length || recHistory.length ? (
                <div className="table-wrap"><table className="dgrid num-grid">
                  <thead><tr><th>No.</th><th>Description</th><th>Type</th><th>Status</th><th>Severity</th><th>Updated</th></tr></thead>
                  <tbody>{[...recActive, ...recHistory].map((r) => <tr key={r.id}><td><Link to={`/r/${r.number}`}>{r.number}</Link></td><td>{r.title}</td><td>{r.type === "capa" ? "CAPA" : "Issue"}</td><td><StatePill state={r.state} /></td><td><Severity severity={r.severity} /></td><td>{usDate(r.updatedAt)}</td></tr>)}</tbody>
                </table></div>
              ) : <p className="dash-empty" style={{ margin: 0 }}>{hasCustomers ? "No issues or CAPAs for this customer." : "Issues linked to this account's BC customers show here."} Service cases arrive with Customer Service.</p>}
            </XSection>

            <div id="accounting">
              <XSection title="Accounting" chips={hasCustomers ? [overdueInv.length ? `${overdueInv.length} past due, ${usd(overdueInv.reduce((t, d) => t + (d.remaining ?? d.amount ?? 0), 0))} USD` : "Nothing past due", `${openInv.length} open ${openInv.length === 1 ? "invoice" : "invoices"}`] : ["Once the account is a customer in BC"]} open={overdueInv.length > 0}>
                {hasCustomers ? (
                  <>
                    <h3 className="sub-h2">Open Invoices</h3>{docTable(openInv, "invoice", "Document No.", bc ? "Nothing outstanding." : "Loading from Business Central…", true)}
                    {draftInv.length > 0 && <><h3 className="sub-h2">Unposted Invoices</h3>{docTable(draftInv, "invoice", "No.", "")}</>}
                    <h3 className="sub-h2">Paid Invoices</h3>{docTable(pastInv, "invoice", "Document No.", bc ? "No paid invoices yet." : "")}
                    {all("creditMemos").length > 0 && <><h3 className="sub-h2">Credit Memos</h3>{docTable(all("creditMemos"), "creditMemo", "Document No.", "")}</>}
                    {all("shipments").length > 0 && <><h3 className="sub-h2">Shipments</h3>{docTable(all("shipments"), "shipment", "Document No.", "")}</>}
                  </>
                ) : <p className="dash-empty" style={{ margin: 0 }}>Invoices, payments and credit memos come from Business Central once the account is a customer.</p>}
              </XSection>
            </div>

            <XSection title="Account History" chips={[`${a.history.length} ${a.history.length === 1 ? "change" : "changes"}`]} open={false}>
              <table className="dgrid num-grid"><thead><tr><th>Change</th><th>Reason</th><th>By</th><th>Date</th></tr></thead>
                <tbody>{[...a.history].reverse().map((h, i) => <tr key={i}><td>{h.fromState ? `${label(h.fromState)} to ${label(h.toState)}` : "Created"}</td><td>{h.reason && h.reason !== "created" ? h.reason : ""}</td><td>{h.actorName ?? ""}</td><td>{usDate(h.at)}</td></tr>)}</tbody></table>
            </XSection>
          </div>

          <aside className="factboxes" aria-label="FactBoxes">
            <section className="factbox">
              <h2>Customer Statistics</h2>
              {hasCustomers ? (bc ? (
                <dl>
                  <dt>Balance (USD)</dt><dd>{usd(balance)}</dd>
                  <dt>Balance Due (USD)</dt><dd className={overdue > 0 ? "bad" : ""}>{usd(overdue)}</dd>
                  <dt>Sales (USD)</dt><dd>{usd(sales)}</dd>
                  <dt>Open Pipeline (USD)</dt><dd>{usd(openOpps.reduce((t, o) => t + (o.amount ?? 0), 0))}</dd>
                  <dt>Open Sales Orders</dt><dd>{orders.length}</dd>
                  <dt>Open Invoices</dt><dd>{openInv.length}</dd>
                </dl>
              ) : <p className="empty-note">Loading from Business Central…</p>) : <p className="empty-note">Shown once the account is a customer in Business Central. Open pipeline: {usd(openOpps.reduce((t, o) => t + (o.amount ?? 0), 0))} USD.</p>}
            </section>
            <section className="factbox">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}><h2>Contacts</h2><button className="linkish" onClick={() => setPanel("contact")}>New</button></div>
              {a.contacts.length || bcContacts.length ? (
                <table className="fb-table"><tbody>
                  {a.contacts.map((ct) => <tr key={ct.id}><td>{ct.email ? <a href={`mailto:${ct.email}`}>{ct.name}</a> : ct.name}{ct.isPrimary === "yes" ? " (Primary)" : ""}<div className="muted">{ct.title ?? ""}</div></td><td className="r"><button className="linkish" onClick={() => run(() => api.deleteContact(a.id, ct.id))} aria-label={`Remove ${ct.name}`}>Remove</button></td></tr>)}
                  {bcContacts.map((ct) => <tr key={"bc" + ct.id}><td>{ct.name}<div className="muted">Contact {ct.number} in BC</div></td><td /></tr>)}
                </tbody></table>
              ) : <p className="empty-note">No contacts yet.</p>}
            </section>
            {src && hasCustomers && <p className="dash-cap" style={{ margin: 0 }}>Figures are live from {src} as you.</p>}
          </aside>
        </div>
      </div>
      <Peek target={peek} onClose={() => setPeek(null)} />
    </>
  );
}

/** Old record links (/r/ACC-00001) land on the account page. */
export function AccountRedirect({ number }: { number: string }) {
  const nav = useNavigate();
  useEffect(() => { nav(`/accounts/${number}`, { replace: true }); }, [number]);
  return null;
}
